/**
 * Webhooks feature — TanStack Query hooks.
 *
 * Backed by internal/ops/webhook.AdminHandler, mounted at
 * /api/v1/tenants/{tenant_id}/webhooks under the
 * platform-admin gate (WithPathTenant supplies the tenant).
 *
 * The HMAC signing secret lives in the mutation resolved value
 * only. Create and RotateSecret return the plaintext once; every
 * other read returns `secret: null`. The UI reveals it in a
 * dialog and never writes it to any cache.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import { withStepUp } from "@/lib/with-step-up";
import {
  webhookDeliveriesResponseSchema,
  webhookSchema,
  webhooksListResponseSchema,
  type CreateWebhookInput,
  type UpdateWebhookInput,
  type Webhook,
  type WebhookDelivery,
  type WebhooksListResponse,
  type WebhooksListSearch,
} from "./schemas";

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/webhooks`;
}

// ── Fetchers ──────────────────────────────────────────────────────────────

export async function fetchWebhooksList(
  tenantID: string,
  search: WebhooksListSearch,
): Promise<WebhooksListResponse> {
  const raw = await apiFetch<unknown>(base(tenantID), {
    searchParams: {
      q: search.q,
      enabled: search.enabled,
    },
  });
  return webhooksListResponseSchema.parse(raw);
}

export async function fetchWebhook(
  tenantID: string,
  webhookId: string,
): Promise<Webhook> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(webhookId)}`,
  );
  return webhookSchema.parse(raw);
}

export async function fetchWebhookDeliveries(
  tenantID: string,
  webhookId: string,
): Promise<readonly WebhookDelivery[]> {
  const raw = await apiFetch<unknown>(
    `${base(tenantID)}/${encodeURIComponent(webhookId)}/deliveries`,
  );
  return webhookDeliveriesResponseSchema.parse(raw).data;
}

// ── Queries ───────────────────────────────────────────────────────────────

export function useWebhooksQuery(tenantID: string, search: WebhooksListSearch) {
  return useQuery<WebhooksListResponse, ApiError>({
    queryKey: queryKeys.webhooks.list(tenantID),
    queryFn: () => fetchWebhooksList(tenantID, search),
    enabled: tenantID.length > 0,
  });
}

export function useWebhookQuery(tenantID: string, webhookId: string) {
  return useQuery<Webhook, ApiError>({
    queryKey: queryKeys.webhooks.detail(tenantID, webhookId),
    queryFn: () => fetchWebhook(tenantID, webhookId),
    enabled: tenantID.length > 0 && webhookId.length > 0,
  });
}

export function useWebhookDeliveriesQuery(tenantID: string, webhookId: string) {
  return useQuery<readonly WebhookDelivery[], ApiError>({
    queryKey: queryKeys.webhooks.deliveries(tenantID, webhookId),
    queryFn: () => fetchWebhookDeliveries(tenantID, webhookId),
    enabled: tenantID.length > 0 && webhookId.length > 0,
    // Delivery logs accumulate; 15s is a reasonable "live enough"
    // interval for an ops operator monitoring an ongoing issue.
    refetchInterval: 15_000,
  });
}

// ── Mutations ─────────────────────────────────────────────────────────────

export function useCreateWebhookMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<Webhook, ApiError, CreateWebhookInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(base(tenantID), {
          method: "POST",
          body: input,
        }).then((raw) => webhookSchema.parse(raw)),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.webhooks.all(tenantID) });
    },
  });
}

export function useUpdateWebhookMutation(tenantID: string, webhookId: string) {
  const qc = useQueryClient();
  return useMutation<Webhook, ApiError, UpdateWebhookInput>({
    mutationFn: (input) =>
      withStepUp(() =>
        apiFetch<unknown>(`${base(tenantID)}/${encodeURIComponent(webhookId)}`, {
          method: "PATCH",
          body: input,
        }).then((raw) => webhookSchema.parse(raw)),
      ),
    onSuccess: (updated) => {
      qc.setQueryData(queryKeys.webhooks.detail(tenantID, webhookId), updated);
      qc.invalidateQueries({ queryKey: queryKeys.webhooks.list(tenantID) });
    },
  });
}

/** Rotate returns `{ secret }`. Caller is responsible for revealing
 * it in a dialog exactly once. Nothing is written to the cache —
 * the plaintext lives in the resolved value only. */
interface RotateSecretResult {
  secret: string;
}

export function useRotateWebhookSecretMutation(tenantID: string, webhookId: string) {
  const qc = useQueryClient();
  return useMutation<RotateSecretResult, ApiError, void>({
    mutationFn: () =>
      withStepUp(() =>
        apiFetch<RotateSecretResult>(
          `${base(tenantID)}/${encodeURIComponent(webhookId)}/rotate-secret`,
          { method: "POST" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: queryKeys.webhooks.detail(tenantID, webhookId) });
    },
  });
}

export function useRedeliverMutation(tenantID: string, webhookId: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (deliveryId) =>
      withStepUp(() =>
        apiFetch<void>(
          `${base(tenantID)}/${encodeURIComponent(webhookId)}/deliveries/${encodeURIComponent(deliveryId)}/redeliver`,
          { method: "POST" },
        ),
      ),
    onSuccess: () => {
      qc.invalidateQueries({
        queryKey: queryKeys.webhooks.deliveries(tenantID, webhookId),
      });
    },
  });
}

export function useDeleteWebhookMutation(tenantID: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: (webhookId) =>
      withStepUp(() =>
        apiFetch<void>(`${base(tenantID)}/${encodeURIComponent(webhookId)}`, {
          method: "DELETE",
        }),
      ),
    onSuccess: (_res, webhookId) => {
      qc.removeQueries({ queryKey: queryKeys.webhooks.detail(tenantID, webhookId) });
      qc.invalidateQueries({ queryKey: queryKeys.webhooks.list(tenantID) });
    },
  });
}
